/**
 * v2.6.9 batch Y · Rider Flag Banner
 * Shared visual for dispatcher Book-a-Ride form, driver incoming-ride card,
 * and any other surface that needs to surface a flagged-rider warning.
 *
 * Props:
 *   flag = {
 *     severity:   "watch" | "warning" | "block",
 *     flag_count: number,
 *     latest_reason: string,
 *     latest_incident_number: string,
 *     flagged_at: ISO string,
 *     customer_name?: string,
 *   }
 */
import { AlertTriangle, Flag, ShieldOff } from "lucide-react";

const SEVERITY_STYLE = {
  watch: {
    accent: "border-yellow-500/50 bg-yellow-500/10 text-yellow-200",
    icon: AlertTriangle,
    label: "WATCH",
    hint: "Informational — proceed with awareness.",
  },
  warning: {
    accent: "border-orange-500/60 bg-orange-500/10 text-orange-200",
    icon: Flag,
    label: "WARNING",
    hint: "Past incident on record. Use caution.",
  },
  block: {
    accent: "border-red-500/70 bg-red-500/15 text-red-100",
    icon: ShieldOff,
    label: "BLOCKED RIDER",
    hint: "Rider is blocked from future bookings.",
  },
};

export function RiderFlagBanner({ flag, compact = false }) {
  if (!flag) return null;
  const sev = (flag.severity || "warning").toLowerCase();
  const cfg = SEVERITY_STYLE[sev] || SEVERITY_STYLE.warning;
  const Icon = cfg.icon;
  const dateStr = flag.flagged_at ? new Date(flag.flagged_at).toLocaleDateString() : "—";

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${cfg.accent}`}
        title={`${cfg.label} · ${flag.latest_reason || "no reason"} · ${flag.flag_count || 1} flag${(flag.flag_count || 1) === 1 ? "" : "s"} · last ${dateStr}`}
        data-testid="rider-flag-badge"
      >
        <Icon className="h-3 w-3"/> {cfg.label}
      </span>
    );
  }

  return (
    <div
      className={`mb-3 rounded border-2 p-3 ${cfg.accent}`}
      data-testid="rider-flag-banner"
    >
      <div className="flex items-start gap-2">
        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5"/>
        <div className="flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-extrabold text-sm uppercase tracking-wider">🚩 {cfg.label}</span>
            {flag.customer_name && <span className="text-[11px] opacity-80">· {flag.customer_name}</span>}
            <span className="text-[10px] opacity-60 ml-auto">{flag.flag_count || 1} flag{(flag.flag_count || 1) === 1 ? "" : "s"} · last {dateStr}</span>
          </div>
          <div className="text-xs mt-1 italic opacity-95"><b>Reason:</b> {flag.latest_reason || "—"}</div>
          {flag.latest_incident_number && (
            <div className="text-[10px] opacity-70 mt-1">Source incident: <span className="font-mono">{flag.latest_incident_number}</span></div>
          )}
          <div className="text-[10px] opacity-70 mt-1">{cfg.hint}</div>
        </div>
      </div>
    </div>
  );
}
